import { useEffect, useRef } from "react";
import type { LeadCardResponse } from "@gioia365/contracts";
import { useNavigate, useRouterState } from "@tanstack/react-router";
import { firstCallablePhone, useDialerSessionIdFor } from "../dialer/dialer-session";
import { useT } from "../i18n/locale";
import type { Access } from "../session/access";
import { commercialViewOf, type TabContext } from "../session/tab-context";
import { useCallSession } from "../telephony/call-session";

// «Chiama» da una riga dei Task (piano M5, Task 18; ruling Q7 e Q57) e «Chiama adesso» dal banner (Task 20): aprono la scheda
// del Lead con il segno `callFromTasks` nello stato di una voce nuova della cronologia, mai nell'indirizzo. La scheda legge il
// segno una volta per voce (un clic nuovo è una voce nuova, anche sulla scheda già aperta per lo stesso Lead) e lo toglie subito
// dalla voce, anche prima di avere i dati: ricaricare la pagina, tornarci con «Indietro» o «Avanti» o condividere l'indirizzo non
// richiama mai. Appena la scheda c'è, la chiamata parte con `start` di M4, sul primo telefono che si chiama nell'ordine della
// scheda (quello di «Chiama» della testata). Se il Lead non si chiama adesso (nessun telefono, chiamate ferme) o la chiamata del
// browser è già occupata non parte nulla: la scheda dice perché, con «Chiama» spento e il suo motivo; e finché la chiamata è
// occupata «Chiama» dei Task e «Chiama adesso» del banner sono spenti, con lo stesso motivo (`useCallFromTasksBlock`).

/**
 * Chi chiama da una riga dei Task e dal banner del richiamo concordato (Task 20): come il Power Dialer, mai nella «Guarda come…» né
 * nel contesto di piattaforma, mai senza `call` (questione 331). Gli altri hanno «Apri».
 */
export function canCallFromTasks(access: Access, tab: TabContext): boolean {
  return tab.kind === "membership" && commercialViewOf(tab) === null && !access.isPlatform && access.can("call");
}

/**
 * Perché adesso non si chiama dai Task né dal banner: la chiamata di questa scheda del browser è in corso o aspetta il suo esito.
 * Gli stessi testi di «Chiama» della scheda del Lead (M4, `CallButton`); `null` quando si può chiamare.
 */
export function useCallFromTasksBlock(): string | null {
  const t = useT();
  const { session } = useCallSession();
  if (session.phase === "idle" || session.phase === "error") return null;
  return t(session.phase === "outcome" ? "callDesk.busyOutcome" : "callDesk.busyCalling");
}

/** Fa partire la chiamata chiesta da una riga dei Task o dal banner (`state.callFromTasks`, l'identificativo del Task): una per clic. */
export function useCallFromTasks(tab: TabContext, leadId: string, card: LeadCardResponse | null, leadName: string | null): void {
  const requested = useRouterState({ select: (state) => state.location.state.callFromTasks });
  const entry = useRouterState({ select: (state) => state.location.state.__TSR_key });
  const navigate = useNavigate();
  const { session, start } = useCallSession();
  const dialerSessionId = useDialerSessionIdFor(tab, leadId);
  // La voce della cronologia di cui si è già letta la richiesta: anche se l'effetto riparte prima che la voce sia riscritta, la
  // stessa voce non chiama due volte.
  const consumed = useRef<string | null>(null);
  // Una richiesta letta che aspetta i dati della scheda per partire.
  const pending = useRef(false);
  const free = session.phase === "idle" || session.phase === "error";

  useEffect(() => {
    const key = typeof requested === "string" ? (entry ?? requested) : null;
    if (key !== null && consumed.current !== key) {
      consumed.current = key;
      pending.current = true;
      // Subito, anche prima che la scheda ci sia: la voce resta senza la richiesta.
      void navigate({
        to: "/lead/$leadId",
        params: { leadId },
        search: true,
        replace: true,
        state: (previous) => ({ ...previous, callFromTasks: undefined }),
      });
    }
    if (!pending.current || !card || leadName === null) return;
    pending.current = false;
    const phone = firstCallablePhone(card);
    if (phone && free) void start({ tab, leadId, phone, leadName, ...(dialerSessionId === undefined ? {} : { dialerSessionId }) });
  }, [requested, entry, leadId, card, leadName, free, tab, start, navigate, dialerSessionId]);
}
